import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, ArrowsRightLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import LoadingOverlay, { WidgetWrapper } from '@/components/ui/LoadingOverlay';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts';
import { apiClient, isApiError } from '@/lib/api';
import type {
  TTSJob,
  LastRunTelemetryResponse,
  LastRunTelemetrySummary,
  LastRunTelemetryEvent,
  DistributionStatusResponse,
  StepTimingsAveragesResponse,
} from '@/types/api';
import { formatDuration, DISTRIBUTION_PIPELINE_STEPS } from '@/types/api';
import {
  buildPipelineSteps,
  getPipelineStatusDisplay,
  buildSimplifiedTimeline,
} from './telemetryHelpers';

interface StepTimingDatum {
  step: string;
  avgTime: number;
}

interface StepTimingConfig {
  label: string;
  summaryKey: string;
  eventPatterns: string[];
}

// Map pipeline stages to summary keys and event name patterns
const STEP_TIMING_CONFIG: StepTimingConfig[] = [
  {
    label: 'PDF Ingest',
    summaryKey: 'pdf_ingestion',
    eventPatterns: ['pdf_ingestion'],
  },
  {
    label: 'Image Process',
    summaryKey: 'image_processing',
    eventPatterns: ['image_processing', 'image_processor_png', 'extraction_images', 'extract_images'],
  },
  {
    label: 'Chapter Detect',
    summaryKey: 'chapter_detection',
    eventPatterns: ['chapter_detector', 'chapter_detection'],
  },
  {
    label: 'TTS Synth',
    summaryKey: 'synthesize_tts',
    eventPatterns: ['synthesize_tts', 'tts_synthesis', 'speech_synthesis', 'synthesize', 'tts'],
  },
  {
    label: 'Audio Assemble',
    summaryKey: 'assemble_audio',
    eventPatterns: ['assemble_audio', 'merge_audio', 'concat_audio', 'concatenate_audio', 'assemble'],
  },
  {
    label: 'Credits Gen',
    summaryKey: 'generate_credits',
    eventPatterns: ['generate_credits', 'credits_generation', 'credits'],
  },
  {
    label: 'QA Finalize',
    summaryKey: 'qa_finalize',
    eventPatterns: ['qa_finalize', 'qa_final', 'quality_assurance', 'qa', 'finalize'],
  },
];

// Mock memory usage data - NOT CONNECTED yet
type MemoryTimeRange = '1h' | '6h' | '24h';

interface MemoryPoint {
  time: string;
  memory: number;
}

const MEMORY_TIME_RANGES: { value: MemoryTimeRange; label: string }[] = [
  { value: '1h', label: '1H' },
  { value: '6h', label: '6H' },
  { value: '24h', label: '24H' },
];

function generateMemoryData(range: MemoryTimeRange): MemoryPoint[] {
  const now = new Date();
  let intervals: number;
  let stepMs: number;

  switch (range) {
    case '1h':
      // 5-minute intervals
      intervals = 12;
      stepMs = 5 * 60 * 1000;
      break;
    case '6h':
      // 15-minute intervals
      intervals = 24;
      stepMs = 15 * 60 * 1000;
      break;
    case '24h':
      // 1-hour intervals
      intervals = 24;
      stepMs = 60 * 60 * 1000;
      break;
    default:
      intervals = 12;
      stepMs = 5 * 60 * 1000;
  }

  const points: MemoryPoint[] = [];

  for (let i = intervals; i >= 0; i -= 1) {
    const time = new Date(now.getTime() - i * stepMs);
    const label = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Generate realistic memory usage (roughly 60–200MB) with smooth variation
    const phase = (intervals - i) / Math.max(1, intervals);
    const wave = Math.sin(phase * Math.PI * 2); // -1..1
    const base = 120; // base memory usage
    const amplitude = 50; // how much it can swing
    const noise = (Math.random() - 0.5) * 10; // small random jitter
    const raw = base + wave * amplitude + noise;
    const clamped = Math.min(200, Math.max(50, Math.round(raw)));

    points.push({ time: label, memory: clamped });
  }

  return points;
}

function parseDateSafe(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

function computeStageDurationMsFromSummary(
  summary: LastRunTelemetrySummary | undefined,
  key: string
): number | null {
  if (!summary) return null;
  const anySummary = summary as Record<string, unknown>;
  const stage = anySummary[key] as
    | {
        started?: string | null;
        started_at?: string | null;
        completed?: string | null;
        completed_at?: string | null;
        duration_ms?: number | null;
      }
    | undefined;

  if (!stage) return null;

  if (typeof stage.duration_ms === 'number' && stage.duration_ms > 0) {
    return stage.duration_ms;
  }

  const start = parseDateSafe(stage.started ?? stage.started_at);
  const end = parseDateSafe(stage.completed ?? stage.completed_at);
  if (!start || !end) return null;
  const diff = end.getTime() - start.getTime();
  return diff > 0 ? diff : null;
}

function computeStageDurationMsFromEvents(
  events: LastRunTelemetryEvent[] | undefined,
  patterns: string[]
): number | null {
  if (!events || events.length === 0) return null;
  const lowered = patterns.map((p) => p.toLowerCase());

  const matches = (eventName: string, suffix: string) => {
    const name = (eventName || '').toLowerCase();
    return lowered.some((p) => name.includes(`${p}_${suffix}`));
  };

  let start: Date | null = null;
  let end: Date | null = null;

  events.forEach((e) => {
    const ts = parseDateSafe(e.timestamp);
    if (!ts) return;
    const name = e.event_name || '';
    if (matches(name, 'start')) {
      if (!start || ts < start) start = ts;
    }
    if (matches(name, 'end')) {
      if (!end || ts > end) end = ts;
    }
  });

  if (!start || !end) return null;
  const startMs = (start as Date).getTime();
  const endMs = (end as Date).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  const diff = endMs - startMs;
  return diff > 0 ? diff : null;
}

function buildStepTimingsFromLastRun(
  lastRun: LastRunTelemetryResponse | null
): StepTimingDatum[] {
  if (!lastRun || !lastRun.summary) return [];
  const summary = lastRun.summary as LastRunTelemetrySummary;
  const events = lastRun.events;

  return STEP_TIMING_CONFIG.map((cfg) => {
    const summaryMs = computeStageDurationMsFromSummary(summary, cfg.summaryKey);
    const eventsMs =
      summaryMs == null
        ? computeStageDurationMsFromEvents(events, cfg.eventPatterns)
        : null;

    const durationMs = summaryMs ?? eventsMs ?? null;
    const minutes =
      durationMs && durationMs > 0 ? Number((durationMs / 60000).toFixed(1)) : 0;

    return {
      step: cfg.label,
      avgTime: minutes,
    };
  });
}

function isQaCompleted(
  summary: LastRunTelemetrySummary | undefined,
  events: LastRunTelemetryEvent[] | undefined
): boolean {
  try {
    const qa = summary && (summary.qa_finalize as {
      status?: unknown;
      completed?: unknown;
      completed_at?: unknown;
    });

    if (qa) {
      const rawStatus = (qa.status ?? '').toString().toLowerCase();
      if (['completed', 'done', 'success', 'finished', 'complete'].includes(rawStatus)) {
        return true;
      }
      if (!rawStatus && (qa.completed || qa.completed_at)) {
        return true;
      }
    }

    if (Array.isArray(events)) {
      const hit = events.some((e) => {
        const name = (e.event_name || '').toLowerCase();
        return (
          name.includes('qa_finalize_end') ||
          name.includes('qa_end') ||
          (name.includes('qa') && name.includes('_end'))
        );
      });
      if (hit) return true;
    }
  } catch {
    // Ignore and fall through
  }
  return false;
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="success">Success</Badge>;
    case 'running':
    case 'processing':
      return <Badge variant="primary">Running</Badge>;
    case 'failed':
      return <Badge variant="danger">Failed</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

export default function TTSTelemetryPage() {
  const [memoryRange, setMemoryRange] = useState<MemoryTimeRange>('1h');
  const [memoryData, setMemoryData] = useState<MemoryPoint[]>(() =>
    generateMemoryData('1h')
  );

  // Real data state
  const [runs, setRuns] = useState<TTSJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Step timing averages state (new aggregated endpoint)
  const [stepAverages, setStepAverages] = useState<StepTimingDatum[]>([]);
  const [, setStepAveragesLoading] = useState(true);
  const [, setStepAveragesError] = useState<string | null>(null);
  const [stepAveragesMeta, setStepAveragesMeta] = useState<{
    windowDays: number;
    runsConsidered: number;
  } | null>(null);

  // Last run telemetry state
  const [lastRun, setLastRun] = useState<LastRunTelemetryResponse | null>(null);
  const [lastRunLoading, setLastRunLoading] = useState(true);
  const [lastRunError, setLastRunError] = useState<string | null>(null);

  // Distribution pipeline tracking (post-QA)
  const [distributionStatus, setDistributionStatus] = useState<
    Record<string, 'not_started' | 'completed'>
  >({});
  const [distributionIsbn, setDistributionIsbn] = useState<string | null>(null);
  const [, setDistributionLoading] = useState(false);
  const [, setDistributionError] = useState<string | null>(null);
  const [qaCompletedForRun, setQaCompletedForRun] = useState(false);

  // Regenerate mock memory data when the selected range changes
  useEffect(() => {
    setMemoryData(generateMemoryData(memoryRange));
  }, [memoryRange]);

  // Fallback timings from the latest run (used only if averages are unavailable)
  const stepTimingsFromLastRun: StepTimingDatum[] = useMemo(
    () => buildStepTimingsFromLastRun(lastRun),
    [lastRun]
  );

  const stepTimings: StepTimingDatum[] =
    stepAverages.length > 0 ? stepAverages : stepTimingsFromLastRun;

  // Helper to compute duration (ms) for a TTS job
  const computeJobDurationMs = (job: TTSJob): number => {
    const start = parseDateSafe(job.created_at);
    const end = parseDateSafe(job.finished_at || job.updated_at || job.created_at);
    if (!start || !end) return 0;
    const diff = end.getTime() - start.getTime();
    return diff > 0 ? diff : 0;
  };

  // Fetch runs from backend (using the same endpoint as Dashboard)
  const fetchRuns = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      else setIsRefreshing(true);

      const response = await apiClient.get<TTSJob[]>('tts/jobs/history?limit=all');
      // Ensure newest first just in case backend changes ordering
      const sorted = [...response].sort((a, b) => {
        const aDate = parseDateSafe(a.created_at)?.getTime() ?? 0;
        const bDate = parseDateSafe(b.created_at)?.getTime() ?? 0;
        return bDate - aDate;
      });
      setRuns(sorted);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch telemetry runs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch telemetry');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch step timing averages from backend
  const fetchStepAverages = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setStepAveragesLoading(true);

      const data = await apiClient.get<StepTimingsAveragesResponse>(
        'tts/telemetry/step-averages'
      );

      const mapped: StepTimingDatum[] = (data.steps || []).map((step) => {
        // Prefer backend label, otherwise fall back to our local config label, then key
        const cfgLabel =
          STEP_TIMING_CONFIG.find((cfg) => cfg.summaryKey === step.key)?.label ??
          step.key;
        const label = step.label || cfgLabel;

        const minutes =
          typeof step.avg_duration_minutes === 'number'
            ? step.avg_duration_minutes
            : typeof step.avg_duration_ms === 'number'
            ? Number((step.avg_duration_ms / 60000).toFixed(2))
            : 0;

        return {
          step: label,
          avgTime: minutes,
        };
      });

      setStepAverages(mapped);
      setStepAveragesMeta({
        windowDays: data.window_days,
        runsConsidered: data.runs_considered,
      });
      setStepAveragesError(null);
    } catch (err) {
      console.error('Failed to fetch step timing averages:', err);
      setStepAveragesError(
        err instanceof Error ? err.message : 'Failed to fetch step timing averages'
      );
    } finally {
      setStepAveragesLoading(false);
    }
  }, []);

  // Fetch last run telemetry from backend (proxied to /telemetry/last)
  const fetchLastRunTelemetry = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLastRunLoading(true);

      const data = await apiClient.get<LastRunTelemetryResponse>('tts/telemetry/last');

      if (data && data.error) {
        setLastRun(null);
        setLastRunError(data.error);
      } else {
        setLastRun(data);
        setLastRunError(null);
      }
    } catch (err) {
      console.error('Failed to fetch last run telemetry:', err);
      setLastRunError(
        err instanceof Error ? err.message : 'Failed to fetch last run telemetry'
      );
    } finally {
      setLastRunLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchRuns();
    fetchLastRunTelemetry();
    fetchStepAverages();
  }, [fetchRuns, fetchLastRunTelemetry, fetchStepAverages]);

  // Poll for updates every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRuns(false);
      fetchLastRunTelemetry(false);
      fetchStepAverages(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchRuns, fetchLastRunTelemetry, fetchStepAverages]);

  // Track QA completion and resolve ISBN for the last run
  useEffect(() => {
    if (!lastRun || !lastRun.run_id) {
      setQaCompletedForRun(false);
      setDistributionIsbn(null);
      setDistributionStatus({});
      setDistributionError(null);
      return;
    }

    const completed = isQaCompleted(
      lastRun.summary as LastRunTelemetrySummary | undefined,
      lastRun.events
    );
    setQaCompletedForRun(completed);

    if (!completed) {
      setDistributionIsbn(null);
      setDistributionStatus({});
      setDistributionError(null);
      return;
    }

    // Already resolved for this run
    if (distributionIsbn) return;

    const jobId = lastRun.run_id;

    (async () => {
      try {
        // Step 1 – Get job metadata from TTS worker (proxied through backend)
        const meta = await apiClient.get<{ book_name?: string; isbn?: string }>(
          `tts/qa/${jobId}/metadata`
        );

        // If metadata already includes ISBN, prefer it directly
        if (meta && typeof meta.isbn === 'string' && meta.isbn) {
          setDistributionIsbn(meta.isbn);
          setDistributionError(null);
          return;
        }

        const title = meta?.book_name;
        if (!title) {
          setDistributionError('Book title not available in job metadata');
          return;
        }

        // Step 2 – Resolve ISBN via distribution worker using job_id (proxied)
        const res = await apiClient.get<{ isbn?: string }>(
          `distribution/books/getISBN?job_id=${encodeURIComponent(jobId)}`
        );

        if (res && typeof res.isbn === 'string' && res.isbn) {
          setDistributionIsbn(res.isbn);
          setDistributionError(null);
        } else {
          setDistributionError('ISBN not available yet for this title');
        }
      } catch (err) {
        console.error('Failed to resolve ISBN for last run:', err);
        setDistributionError(
          err instanceof Error ? err.message : 'Failed to resolve ISBN for distribution'
        );
      }
    })();
  }, [lastRun, distributionIsbn]);

  // Poll distribution status once ISBN is known and QA is completed
  useEffect(() => {
    if (!qaCompletedForRun || !distributionIsbn) {
      setDistributionStatus({});
      return;
    }

    let cancelled = false;

    const fetchStatusOnce = async () => {
      try {
        if (cancelled) return;
        const data = await apiClient.get<DistributionStatusResponse>(
          `distribution/status?isbn=${encodeURIComponent(distributionIsbn)}`
        );
        if (cancelled) return;

        const next: Record<string, 'not_started' | 'completed'> = {};
        let allDone = true;

        DISTRIBUTION_PIPELINE_STEPS.forEach((name) => {
          const raw = (data && data[name]) || '';
          const done = raw.toString().toUpperCase() === 'DONE';
          next[name] = done ? 'completed' : 'not_started';
          if (!done) allDone = false;
        });

        setDistributionStatus(next);
        setDistributionError(null);

        if (allDone) {
          cancelled = true;
        }
      } catch (err) {
        if (cancelled) return;
        // Treat 404 as "not ready yet" and keep polling quietly (no user-facing error),
        // mirroring the legacy frontend behavior.
        if (isApiError(err) && err.status === 404) {
          return;
        }
        console.error('Failed to fetch distribution status:', err);
        setDistributionError(
          err instanceof Error ? err.message : 'Failed to fetch distribution status'
        );
      } finally {
        if (!cancelled) {
          setDistributionLoading(false);
        }
      }
    };

    setDistributionLoading(true);
    fetchStatusOnce();

    const interval = setInterval(() => {
      if (!cancelled) {
        fetchStatusOnce();
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [qaCompletedForRun, distributionIsbn]);

  // Calculate stats from real data
  const totalRuns = runs.length;
  const completedRuns = runs.filter((r) => r.status === 'completed').length;
  const successRate =
    (completedRuns / Math.max(1, runs.length)) * 100;
  const avgDurationMs =
    runs.reduce((sum, r) => sum + computeJobDurationMs(r), 0) /
    Math.max(1, runs.length);
  const avgDuration = formatDuration(avgDurationMs);
  const activeRuns = runs.filter(
    (r) => r.status === 'processing' || r.status === 'running'
  ).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pipeline Telemetry</h1>
          <p className="text-slate-500 mt-1">Monitor performance and analyze pipeline runs</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
            onClick={() => {
              fetchRuns(false);
              fetchLastRunTelemetry(false);
              fetchStepAverages(false);
            }}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
          <Link to="/tts/telemetry/compare">
            <Button
              variant="outline"
              leftIcon={<ArrowsRightLeftIcon className="w-4 h-4" />}
              disabled
            >
              Compare Runs
            </Button>
          </Link>
        </div>
      </div>

      {/* Last Run Telemetry - Connected to real /tts/telemetry/last */}
      <LoadingOverlay isLoading={lastRunLoading} loadingOpacity={40}>
        <Card>
          <CardHeader
            title="Last Run Telemetry"
            subtitle="Latest pipeline execution overview"
            action={
              <div className="flex gap-2">
                {lastRun?.run_id && (
                  <Link to={`/tts/telemetry/${lastRun.run_id}`}>
                    <Button variant="outline" size="sm" leftIcon={<EyeIcon className="w-4 h-4" />}>
                      Details
                    </Button>
                  </Link>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={
                    <ArrowPathIcon
                      className={`w-4 h-4 ${lastRunLoading ? 'animate-spin' : ''}`}
                    />
                  }
                  onClick={() => fetchLastRunTelemetry(false)}
                >
                  Refresh
                </Button>
              </div>
            }
          />
          <CardBody>
            {lastRunError && !lastRun && !lastRunLoading && (
              <div className="text-center py-6">
                <p className="text-sm text-red-500 mb-2">Telemetry Not Available</p>
                <p className="text-xs text-slate-500 mb-4">
                  The last-run telemetry endpoint is not available or returned an error.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLastRunTelemetry(true)}
                  leftIcon={<ArrowPathIcon className="w-4 h-4" />}
                >
                  Try Again
                </Button>
              </div>
            )}

            {!lastRunError && !lastRun && !lastRunLoading && (
              <div className="text-center py-6 text-slate-500">
                No telemetry data available yet.
              </div>
            )}

            {lastRun && (
              <div className="space-y-6">
                {/* Overview + Pipeline Summary */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Run Overview */}
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                      Run Overview
                    </h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Run</dt>
                        <dd className="font-medium text-slate-800">
                          {lastRun.run_id ? `Run ${lastRun.run_id}` : '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Run ID</dt>
                        <dd className="font-mono text-xs text-slate-700 break-all">
                          {lastRun.run_id || '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Status</dt>
                        <dd className="text-slate-800">
                          {getPipelineStatusDisplay(
                            buildPipelineSteps(lastRun.summary, lastRun.events, lastRun.status)
                          )}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Events</dt>
                        <dd className="text-slate-800">
                          {typeof lastRun.total_events === 'number'
                            ? `${lastRun.total_events} events`
                            : '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Pipeline Summary */}
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                      Pipeline Summary
                    </h3>
                    {(() => {
                      const coreSteps = buildPipelineSteps(
                        lastRun.summary,
                        lastRun.events,
                        lastRun.status
                      );
                      const steps = [...coreSteps];

                      if (qaCompletedForRun && DISTRIBUTION_PIPELINE_STEPS.length > 0) {
                        DISTRIBUTION_PIPELINE_STEPS.forEach((name) => {
                          const status = distributionStatus[name] || 'not_started';
                          steps.push({
                            key: `dist_${name}`,
                            label: name,
                            status,
                          });
                        });
                      }

                      const completedCount = steps.filter(
                        (s) => s.status === 'completed'
                      ).length;
                      return (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between gap-3 overflow-x-auto pb-2">
                            {steps.map((step, index) => {
                              const isLast = index === steps.length - 1;
                              const isCompleted = step.status === 'completed';
                              const isInProgress = step.status === 'in_progress';
                              const baseCircle =
                                'w-9 h-9 flex items-center justify-center rounded-full border-2 text-xs font-semibold';

                              const circleClass =
                                step.status === 'completed'
                                  ? 'border-emerald-500 bg-emerald-500 text-white'
                                  : step.status === 'in_progress'
                                  ? 'border-primary-500 bg-primary-50 text-primary-600'
                                  : step.status === 'failed'
                                  ? 'border-danger-500 bg-danger-50 text-danger-600'
                                  : 'border-slate-200 bg-white text-slate-400';

                              return (
                                <div
                                  key={step.key}
                                  className="flex items-center flex-1 min-w-[120px]"
                                >
                                  <div className="flex flex-col items-center">
                                    <div className={`${baseCircle} ${circleClass}`}>
                                      {isCompleted ? (
                                        <span>✓</span>
                                      ) : (
                                        <span>{index + 1}</span>
                                      )}
                                    </div>
                                    <p className="mt-2 text-xs font-medium text-slate-700 text-center">
                                      {step.label}
                                    </p>
                                    <p className="text-[11px] text-slate-500 capitalize">
                                      {step.status.replace('_', ' ')}
                                    </p>
                                  </div>
                                  {!isLast && (
                                    (() => {
                                      const nextStep = steps[index + 1];
                                      const pairKey = `${step.label}__${nextStep?.label}`;
                                      const isPrimaryConnector =
                                        pairKey === 'Extraction (Images PNG)__Chapter Detection' ||
                                        pairKey === 'Chapter Detection__Assemble';

                                      return (
                                        <div
                                          className={`flex-1 h-0.5 mx-2 ${
                                            isPrimaryConnector
                                              ? 'bg-emerald-500'
                                              : isCompleted
                                              ? 'bg-slate-300'
                                              : isInProgress
                                              ? 'bg-primary-400'
                                              : 'bg-slate-200'
                                          }`}
                                        />
                                      );
                                    })()
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Segmented progress bar */}
                          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="flex h-full w-full">
                              {steps.map((step, index) => {
                                const isCompleted = index < completedCount;
                                const isCurrent =
                                  index === completedCount &&
                                  steps[index].status === 'in_progress';
                                const segmentWidth = `${100 / steps.length}%`;
                                const segmentColor = isCompleted
                                  ? 'bg-emerald-500'
                                  : isCurrent
                                  ? 'bg-primary-400'
                                  : 'bg-slate-200';
                                return (
                                  <div
                                    key={step.key}
                                    className={segmentColor}
                                    style={{ width: segmentWidth }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Pipeline Timeline */}
                <div className="rounded-2xl border border-slate-100 bg-white">
                  <div className="border-b border-slate-100 px-4 py-3">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <span>Pipeline Timeline</span>
                      <span className="text-xs font-normal text-slate-400">
                        Recent key events (last few)
                      </span>
                    </h3>
                  </div>
                  <div className="px-4 py-3">
                    {(() => {
                      const items = buildSimplifiedTimeline(lastRun.events);
                      if (items.length === 0) {
                        return (
                          <p className="text-sm text-slate-500 text-center">
                            No key events yet.
                          </p>
                        );
                      }
                      return (
                        <div className="space-y-3 max-h-56 overflow-y-auto">
                          {items.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-start gap-3 text-sm"
                            >
                              <span className="font-mono text-xs text-slate-400 w-32">
                                {item.timestamp}
                              </span>
                              <span
                                className={`mt-1.5 h-2 w-2 rounded-full ${
                                  item.isEnd ? 'bg-emerald-500' : 'bg-primary-400'
                                }`}
                              />
                              <div>
                                <p className="font-medium text-slate-700">
                                  {item.label}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {item.isEnd ? '✓ Completed' : '→ Started'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </LoadingOverlay>

      {/* Stats Cards - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40} showSpinner={false}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-slate-800">{totalRuns.toLocaleString()}</p>
              <p className="text-sm text-slate-500 mt-1">Total Runs</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-slate-800">{avgDuration}</p>
              <p className="text-sm text-slate-500 mt-1">Avg Duration</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-success-500">{successRate.toFixed(1)}%</p>
              <p className="text-sm text-slate-500 mt-1">Success Rate</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-primary-500">{activeRuns}</p>
              <p className="text-sm text-slate-500 mt-1">Active Runs</p>
            </CardBody>
          </Card>
        </div>
      </LoadingOverlay>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Memory Usage Over Time */}
        <WidgetWrapper isConnected>
          <Card>
            <CardHeader
              title="Memory Usage Over Time"
              action={
                <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-0.5">
                  {MEMORY_TIME_RANGES.map((range) => {
                    const isActive = memoryRange === range.value;
                    return (
                      <button
                        key={range.value}
                        type="button"
                        onClick={() => setMemoryRange(range.value)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          isActive
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white'
                        }`}
                      >
                        {range.label}
                      </button>
                    );
                  })}
                </div>
              }
            />
            <CardBody>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={memoryData}>
                    <defs>
                      <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4AC3C3" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#4AC3C3" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="time" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="memory"
                      stroke="#2563EB"
                      strokeWidth={2}
                      fill="url(#memoryGradient)"
                      name="Memory (MB)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </WidgetWrapper>

        {/* Step Timings (averages over recent runs, with last-run fallback) */}
        <WidgetWrapper isConnected>
          <Card>
            <CardHeader
              title={
                stepAveragesMeta
                  ? 'Step Timings (Averages)'
                  : 'Step Timings (Last Run)'
              }
              subtitle={
                stepAveragesMeta
                  ? `Average minutes per pipeline step over the last ${stepAveragesMeta.windowDays} days (${stepAveragesMeta.runsConsidered} runs)`
                  : 'Minutes per pipeline step in the latest run'
              }
            />
            <CardBody>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stepTimings} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#94A3B8" fontSize={12} />
                    <YAxis
                      type="category"
                      dataKey="step"
                      stroke="#94A3B8"
                      fontSize={11}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                      }}
                    />
                    <Bar dataKey="avgTime" fill="#4AC3C3" radius={[0, 4, 4, 0]} name="Avg Time (min)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </WidgetWrapper>
      </div>

      {/* Recent Runs Table - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40}>
        <Card>
          <CardHeader title="Recent Runs" subtitle="Latest pipeline executions" />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow isHoverable={false}>
                  <TableHeaderCell>Run ID</TableHeaderCell>
                  <TableHeaderCell>Job</TableHeaderCell>
                  <TableHeaderCell>Duration</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Started</TableHeaderCell>
                  <TableHeaderCell>Completed</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableEmpty
                    message="Failed to load telemetry"
                    description={error}
                    colSpan={7}
                  />
                ) : runs.length === 0 && !isLoading ? (
                  <TableEmpty
                    message="No telemetry runs found"
                    description="Pipeline runs will appear here once jobs are processed"
                    colSpan={7}
                  />
                ) : (
                  runs.map((run) => {
                    const durationMs = computeJobDurationMs(run);
                    return (
                      <TableRow key={run.id}>
                        <TableCell className="font-mono text-sm">{run.job_id}</TableCell>
                        <TableCell>
                          <p className="font-medium text-slate-800">{run.job_id}</p>
                          <p className="text-xs text-slate-400">{run.id}</p>
                        </TableCell>
                        <TableCell>
                          {durationMs > 0 ? formatDuration(durationMs) : '--'}
                        </TableCell>
                        <TableCell>{getStatusBadge(run.status)}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {run.created_at ? new Date(run.created_at).toLocaleString() : '--'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {run.finished_at
                            ? new Date(run.finished_at).toLocaleString()
                            : '--'}
                        </TableCell>
                        <TableCell>
                          <Link to={`/tts/telemetry/${run.job_id}`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<EyeIcon className="w-4 h-4" />}
                            >
                              View
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </LoadingOverlay>
    </div>
  );
}
